import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../../../components/table";
import type { UserFilters, UserItem } from "../../types/user.types";
import { buildUserListColumns, type UserListTableLabels } from "./userListTableColumns";

export type UsersDataTableProps = {
  users: UserItem[];
  filters: UserFilters;
  onFiltersChange: React.Dispatch<React.SetStateAction<UserFilters>>;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onDeactivate: (user: UserItem) => void;
  roleLabels: Record<string, string>;
  getStatusColor?: (status: string) => string;
  getRoleColor?: (role: string) => string;
  labels: UserListTableLabels;
};

export function UsersDataTable({
  users,
  filters,
  onFiltersChange,
  loading,
  error,
  page,
  totalPages,
  onPageChange,
  onRetry,
  onDeactivate,
  roleLabels,
  getStatusColor,
  getRoleColor,
  labels,
}: UsersDataTableProps) {
  const navigate = useNavigate();
  const navigateToUpdate = useCallback(
    (user: UserItem) => navigate(`/users/update/${user.id}`),
    [navigate],
  );
  const columns = useMemo(
    () =>
      buildUserListColumns({
        filters,
        onFiltersChange,
        labels,
        roleLabels,
        getStatusColor,
        getRoleColor,
        onDeactivate,
        navigateToUpdate,
      }),
    [
      filters,
      onFiltersChange,
      labels,
      roleLabels,
      getStatusColor,
      getRoleColor,
      onDeactivate,
      navigateToUpdate,
    ],
  );

  return (
    <DataTable
      columns={columns}
      dataSource={users}
      rowKey="id"
      loading={loading}
      error={error}
      onRetry={onRetry}
      loadingLabel="Loading users..."
      emptyLabel="Currently no users available."
      // onRowClick={navigateToUpdate}
      pagination={{ page, totalPages, onPageChange }}
      containerClassName="max-h-[calc(100vh-24rem)] overflow-auto"
    />
  );
}
